import { useQuery } from '@tanstack/react-query';
import moment from 'moment/moment';
import { type UseQueryResult } from '@tanstack/react-query';
import { type DailyEcoRating, type Filters, type TripsEcoRatingViewModel, type DateRange } from '../../types';

import { convertFilterToQuery } from '../../utils/convert-filters-to-query';
import { apiClient } from '../../services/ApiClient';
import { getMomentsFromRange } from '../../utils/date-range-utils';
import { barChartDateFormat } from '../../components/BarChartDatePicker/constants';
import { formatDateRange } from '../../components/BarChartDatePicker/utils/bar-chart-utils';
import { type OperationViewModel } from '../../components/Map/types';
import { getLocalTimestampFromUtcDate } from '../../utils/date-utils';
import { ApiEndpoints } from '../../constants/api-constants';
import { Queries, QueryCacheConfig } from '../../constants/query-constants';

export const useFetchTripsEcoRatings = (
  filters: Filters,
  dateRange: DateRange,
  operation: OperationViewModel
): UseQueryResult<TripsEcoRatingViewModel, Error> => {
  const query = {
    ...filters,
    startDate: getLocalTimestampFromUtcDate(dateRange.from, operation?.timezone).toDate().toISOString(),
    endDate: getLocalTimestampFromUtcDate(dateRange.to, operation?.timezone).toDate().toISOString(),
  };
  const queryString = convertFilterToQuery(query);

  const formatEcoRatingsByDay = (ecoRatings: DailyEcoRating[], dateRange: DateRange): DailyEcoRating[] => {
    const dates = getMomentsFromRange(dateRange, 'day');
    return dates.map(day => {
      const rating = ecoRatings.find(e => moment(e.day).isSame(day, 'day'));

      return { ...rating, day: day.format(barChartDateFormat) };
    });
  };

  const getTripsEcoRatings = async (): Promise<TripsEcoRatingViewModel> => {
    const response = await apiClient.get<TripsEcoRatingViewModel>(ApiEndpoints.trips.getEcoRatings(queryString));

    return response && { ...response, dailyRatings: formatEcoRatingsByDay(response.dailyRatings, dateRange) };
  };

  return useQuery<TripsEcoRatingViewModel, Error>({
    ...QueryCacheConfig[Queries.TripsEcoRating],
    queryKey: [Queries.TripsEcoRating, formatDateRange(dateRange), queryString],
    queryFn: getTripsEcoRatings,
  });
};
